import React, { useEffect, useRef, useState } from 'react';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { Pause, Play, RefreshCw } from 'lucide-react';
import { format, formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';

const isSameDayLocal = (d) => {
  try {
    const x = new Date(d);
    const now = new Date();
    return x.getFullYear() === now.getFullYear() &&
      x.getMonth() === now.getMonth() &&
      x.getDate() === now.getDate();
  } catch { return false; }
};

const getInitials = (name = '') =>
  name.split(' ').filter(Boolean).slice(0, 2).map(n => n[0].toUpperCase()).join('');

const getNameFontSize = (count) => {
  if (count <= 2) return '3.4rem';
  if (count <= 4) return '2.6rem';
  if (count <= 8) return '2rem';
  return '1.5rem';
};

const getAvatarSize = (count) => {
  if (count <= 2) return 96;
  if (count <= 4) return 76;
  if (count <= 8) return 60;
  return 48;
};

const getInitialsFontSize = (count) => {
  if (count <= 2) return '2.2rem';
  if (count <= 4) return '1.7rem';
  if (count <= 8) return '1.3rem';
  return '1rem';
};

const getGridCols = (count) => {
  if (count === 1) return 1;
  if (count <= 4) return 2;
  if (count <= 9) return 3;
  return 4;
};

const AVATAR_COLORS = [
  '#3b82f6', '#8b5cf6', '#10b981', '#f59e0b',
  '#ef4444', '#06b6d4', '#ec4899', '#14b8a6',
  '#f97316', '#6366f1', '#84cc16', '#a855f7',
];

const Acolhimento = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('visitantes');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [visitors, setVisitors] = useState([]);
  const [pedidos, setPedidos] = useState([]);
  const hiddenIdsRef = useRef(new Set());   // IDs já removidos definitivamente
  const pendingLidoRef = useRef(new Set()); // IDs marcados aguardando 5 min
  const [autoRefresh, setAutoRefresh] = useState(true);
  const intervalRef = useRef(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  const hasAccess = Boolean(user?.permissions?.fichasEnabled) ||
    ['secretario', 'pastor', 'administracao'].includes(user?.role);

  const fetchVisitors = async () => {
    try {
      setLoading(true);
      setError(null);
      if (!hasAccess) { setVisitors([]); setLastUpdated(new Date()); return; }
      const res = await api.get('/members/visitantes');
      const all = Array.isArray(res.data) ? res.data : [];
      const today = all
        .filter(v => isSameDayLocal(v.visitDate || v.createdAt))
        .sort((a, b) => new Date(b.createdAt || b.visitDate) - new Date(a.createdAt || a.visitDate));
      setVisitors(today);
      setLastUpdated(new Date());
    } catch (e) {
      setError('Falha ao carregar visitantes');
    } finally {
      setLoading(false);
    }
  };

  const fetchPedidos = async () => {
    try {
      if (!hasAccess) return;
      const res = await api.get('/pedidos-oracao');
      const all = Array.isArray(res.data) ? res.data : [];
      // Mostra: não lidos + os recém-marcados que ainda aguardam 5 min
      setPedidos(all.filter(p =>
        !hiddenIdsRef.current.has(p._id) &&
        (!p.lido || pendingLidoRef.current.has(p._id))
      ));
      setLastUpdated(new Date());
    } catch { /* silent */ }
  };

  const marcarLido = async (id) => {
    try {
      await api.patch(`/pedidos-oracao/${id}/lido`);
      pendingLidoRef.current.add(id);
      setPedidos(prev => prev.map(p => p._id === id ? { ...p, lido: true } : p));
      setTimeout(() => {
        pendingLidoRef.current.delete(id);
        hiddenIdsRef.current.add(id);
        setPedidos(prev => prev.filter(p => p._id !== id));
      }, 5 * 60 * 1000);
    } catch { /* silent */ }
  };

  const fetchAll = () => {
    fetchVisitors();
    fetchPedidos();
  };

  useEffect(() => { fetchAll(); }, [hasAccess]);

  useEffect(() => {
    if (autoRefresh && hasAccess) {
      if (intervalRef.current) clearInterval(intervalRef.current);
      intervalRef.current = setInterval(fetchAll, 5000);
    } else {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    return () => { clearInterval(intervalRef.current); intervalRef.current = null; };
  }, [autoRefresh, hasAccess]);

  const count = visitors.length;
  const naoLidos = pedidos.filter(p => !p.lido).length;

  return (
    <div className="acolhimento-board">
      {/* Topo */}
      <div className="acolhimento-topbar">
        <div>
          <h1 className="acolhimento-title">Acolhimento</h1>
          <p className="acolhimento-date" style={{ textTransform: 'capitalize' }}>
            {format(new Date(), "EEEE, d 'de' MMMM 'de' yyyy", { locale: ptBR })}
          </p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          {autoRefresh && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <div className="acolhimento-live-dot" />
              <span style={{ fontSize: '11px', color: 'rgba(255,255,255,0.55)', fontWeight: '700', letterSpacing: '1px' }}>AO VIVO</span>
            </div>
          )}
          <button className="acolhimento-btn" onClick={() => setAutoRefresh(p => !p)}>
            {autoRefresh ? <><Pause size={14} /> Pausar</> : <><Play size={14} /> Ativar</>}
          </button>
          <button className="acolhimento-btn" onClick={fetchAll} disabled={loading}>
            <RefreshCw size={14} style={loading ? { animation: 'spin 0.8s linear infinite' } : {}} />
            Atualizar
          </button>
        </div>
      </div>

      {/* Abas */}
      <div style={{ display: 'flex', gap: '8px', padding: '16px 24px 0', justifyContent: 'center' }}>
        {[
          { id: 'visitantes', label: 'Visitantes', badge: null },
          { id: 'pedidos', label: 'Pedidos de Oração', badge: naoLidos > 0 ? naoLidos : null },
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            style={{
              padding: '8px 20px',
              borderRadius: '20px',
              border: 'none',
              cursor: 'pointer',
              fontSize: '13px',
              fontWeight: '600',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              background: activeTab === tab.id ? 'rgba(255,255,255,0.2)' : 'rgba(255,255,255,0.06)',
              color: activeTab === tab.id ? '#ffffff' : 'rgba(255,255,255,0.45)',
              transition: 'all 0.2s',
            }}
          >
            {tab.label}
            {tab.badge && (
              <span style={{
                background: '#ef4444', color: '#fff',
                borderRadius: '10px', padding: '1px 7px',
                fontSize: '11px', fontWeight: '700',
              }}>{tab.badge}</span>
            )}
          </button>
        ))}
      </div>

      {/* ── ABA VISITANTES ── */}
      {activeTab === 'visitantes' && (
        <>
          <div style={{ textAlign: 'center', padding: '28px 24px 8px' }}>
            <p style={{ margin: 0, fontSize: '15px', fontWeight: '700', color: '#fbbf24', letterSpacing: '3px', textTransform: 'uppercase' }}>
              Seja bem-vindo(a)!
            </p>
            {count > 0 && (
              <p style={{ margin: '6px 0 0', fontSize: '13px', color: 'rgba(255,255,255,0.35)' }}>
                {count} visitante{count !== 1 ? 's' : ''} registrado{count !== 1 ? 's' : ''} hoje
              </p>
            )}
          </div>

          <div className="acolhimento-names-area">
            {loading && count === 0 && (
              <div className="acolhimento-center-state">
                <div className="acolhimento-spinner" />
                <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: '14px', margin: 0 }}>Carregando visitantes…</p>
              </div>
            )}
            {!loading && error && (
              <div className="acolhimento-center-state">
                <p style={{ color: '#f87171', fontSize: '14px' }}>{error}</p>
              </div>
            )}
            {!hasAccess && !loading && (
              <div className="acolhimento-center-state">
                <p style={{ color: 'rgba(255,255,255,0.3)', fontSize: '14px' }}>Sem permissão para visualizar visitantes</p>
              </div>
            )}
            {hasAccess && !loading && !error && count === 0 && (
              <div className="acolhimento-center-state">
                <div style={{ fontSize: '60px', marginBottom: '16px' }}>🙏</div>
                <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: '20px', fontWeight: '600', margin: '0 0 8px' }}>
                  Nenhum visitante registrado hoje
                </p>
                <p style={{ color: 'rgba(255,255,255,0.3)', fontSize: '13px', margin: 0 }}>
                  Os nomes aparecerão aqui automaticamente
                </p>
              </div>
            )}
            {hasAccess && !error && count > 0 && (
              <div style={{
                display: 'grid',
                gridTemplateColumns: `repeat(${getGridCols(count)}, 1fr)`,
                gap: '16px', maxWidth: '1200px', margin: '0 auto', width: '100%',
              }}>
                {visitors.map((v, idx) => (
                  <div key={v._id || idx} className="acolhimento-card">
                    <div style={{
                      width: getAvatarSize(count), height: getAvatarSize(count),
                      borderRadius: '50%', background: AVATAR_COLORS[idx % AVATAR_COLORS.length],
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: getInitialsFontSize(count), fontWeight: '800', color: '#fff',
                      marginBottom: '16px', flexShrink: 0,
                      boxShadow: `0 0 28px ${AVATAR_COLORS[idx % AVATAR_COLORS.length]}66`,
                    }}>
                      {getInitials(v.name)}
                    </div>
                    <p style={{
                      margin: 0, fontSize: getNameFontSize(count), fontWeight: '700',
                      color: '#ffffff', lineHeight: 1.15, wordBreak: 'break-word',
                      textShadow: '0 2px 12px rgba(0,0,0,0.5)', textAlign: 'center',
                    }}>
                      {v.name || '-'}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}

      {/* ── ABA PEDIDOS DE ORAÇÃO ── */}
      {activeTab === 'pedidos' && (
        <div className="acolhimento-names-area">
          {!hasAccess && (
            <div className="acolhimento-center-state">
              <p style={{ color: 'rgba(255,255,255,0.3)', fontSize: '14px' }}>Sem permissão para visualizar pedidos</p>
            </div>
          )}

          {hasAccess && pedidos.length === 0 && (
            <div className="acolhimento-center-state">
              <div style={{ fontSize: '60px', marginBottom: '16px' }}>🙏</div>
              <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: '20px', fontWeight: '600', margin: '0 0 8px' }}>
                Nenhum pedido de oração ainda
              </p>
              <p style={{ color: 'rgba(255,255,255,0.3)', fontSize: '13px', margin: 0 }}>
                Os pedidos aparecerão aqui automaticamente
              </p>
            </div>
          )}

          {hasAccess && pedidos.length > 0 && (
            <div style={{
              display: 'grid',
              gridTemplateColumns: `repeat(${getGridCols(pedidos.length)}, 1fr)`,
              gap: '16px',
              maxWidth: '1200px',
              margin: '0 auto',
              width: '100%',
            }}>
              {pedidos.map((p, idx) => (
                <div
                  key={p._id}
                  className="acolhimento-card"
                  style={{ opacity: p.lido ? 0.35 : 1, position: 'relative', justifyContent: 'flex-start', padding: '28px 20px 20px' }}
                >
                  {/* Avatar */}
                  <div style={{
                    width: getAvatarSize(pedidos.length),
                    height: getAvatarSize(pedidos.length),
                    borderRadius: '50%',
                    background: AVATAR_COLORS[idx % AVATAR_COLORS.length],
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: getInitialsFontSize(pedidos.length),
                    fontWeight: '800', color: '#fff',
                    marginBottom: '12px', flexShrink: 0,
                    boxShadow: `0 0 28px ${AVATAR_COLORS[idx % AVATAR_COLORS.length]}66`,
                  }}>
                    {getInitials(p.nome)}
                  </div>

                  {/* Nome + tempo */}
                  <p style={{ margin: '0 0 16px', fontSize: '13px', color: 'rgba(255,255,255,0.45)', fontWeight: '600', textAlign: 'center' }}>
                    {p.nome} · {formatDistanceToNow(new Date(p.createdAt), { addSuffix: true, locale: ptBR })}
                  </p>

                  {/* Texto do pedido — grande */}
                  <p style={{
                    margin: 0,
                    fontSize: getNameFontSize(pedidos.length),
                    fontWeight: '700',
                    color: '#ffffff',
                    lineHeight: 1.2,
                    wordBreak: 'break-word',
                    textShadow: '0 2px 12px rgba(0,0,0,0.5)',
                    textAlign: 'center',
                    flex: 1,
                  }}>
                    {p.pedido}
                  </p>

                  {/* Botão Orei */}
                  {!p.lido && (
                    <button
                      onClick={() => marcarLido(p._id)}
                      style={{
                        marginTop: '20px',
                        padding: '7px 18px', borderRadius: '20px', border: 'none',
                        background: 'rgba(255,255,255,0.15)', color: 'rgba(255,255,255,0.8)',
                        fontSize: '13px', fontWeight: '600', cursor: 'pointer',
                        backdropFilter: 'blur(4px)',
                      }}
                    >
                      🙏 Orei
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Rodapé */}
      <div style={{
        textAlign: 'center', padding: '10px',
        borderTop: '1px solid rgba(255,255,255,0.06)',
        fontSize: '11px', color: 'rgba(255,255,255,0.2)',
      }}>
        {lastUpdated ? `Última atualização: ${format(lastUpdated, 'HH:mm:ss')}` : '—'}
      </div>
    </div>
  );
};

export default Acolhimento;
